import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { useBranding } from "../lib/branding";

// A responder is not an estate member: own session key, own client, no shared interceptors.
// (lib/api.js attaches the member token and redirects to /login on 401, which would be wrong here.)
const KEY = "responder_token";
const client = axios.create({ baseURL: API_BASE, headers: { "Content-Type": "application/json" } });

const errorText = (err) => err?.response?.data?.detail || "Something went wrong. Try again.";

/**
 * The business/agent side of an estate grant (decision S0). Shows the requests an estate has
 * granted this account, each with the director's written purpose, and takes one reply per
 * submission. Replies are recorded as "supplied, unconfirmed": the estate confirms them.
 */
export default function ResponderHome() {
    const brand = useBranding();
    const invite = new URLSearchParams(window.location.search).get("invite");
    const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
    const [form, setForm] = useState({ email: "", password: "", full_name: "" });
    const [requests, setRequests] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [message, setMessage] = useState("");

    const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

    const load = useCallback(() => {
        if (!token) return;
        client
            .get("/responder/requests", auth())
            .then((res) => setRequests(res.data.requests))
            .catch((err) => {
                if (err?.response?.status === 401 || err?.response?.status === 403) {
                    sessionStorage.removeItem(KEY);
                    setToken(null);
                }
                setMessage(errorText(err));
            });
    }, [token, auth]);

    useEffect(load, [load]);

    const signIn = async (e) => {
        e.preventDefault();
        setMessage("");
        try {
            const res = invite
                ? await client.post("/responder/accept", { token: invite, full_name: form.full_name, password: form.password })
                : await client.post("/responder/login", { email: form.email, password: form.password });
            sessionStorage.setItem(KEY, res.data.token);
            setToken(res.data.token);
            if (invite) window.history.replaceState({}, "", "/responder");
        } catch (err) {
            setMessage(errorText(err));
        }
    };

    const send = async (r) => {
        const draft = drafts[`${r.grant_id}/${r.case_id}`] || {};
        const pounds = (draft.amount || "").trim();
        try {
            await client.post(
                `/responder/requests/${r.grant_id}/${r.case_id}/reply`,
                { text: draft.text || "", amount_pence: pounds ? Math.round(parseFloat(pounds) * 100) : null },
                auth(),
            );
            setDrafts({ ...drafts, [`${r.grant_id}/${r.case_id}`]: { sent: true } });
            setMessage("Sent. The estate will review it; until then it is shown as supplied, not confirmed.");
        } catch (err) {
            setMessage(errorText(err));
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 bg-white">
                <form onSubmit={signIn} className="w-full max-w-sm space-y-3" data-testid="responder-signin">
                    <div className="font-heading text-xl font-bold text-slate-900">{brand.product_name}</div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900">
                        {invite ? "Set up your access" : "Sign in"}
                    </h1>
                    {invite ? (
                        <>
                            <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Your name" required
                                value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                            <input className="w-full border rounded px-3 py-2 text-sm" type="password" required minLength={12}
                                placeholder="Choose a password (12+ characters)"
                                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </>
                    ) : (
                        <>
                            <input className="w-full border rounded px-3 py-2 text-sm" type="email" placeholder="Email" required
                                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            <input className="w-full border rounded px-3 py-2 text-sm" type="password" placeholder="Password" required
                                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </>
                    )}
                    <button className="w-full rounded bg-slate-900 text-white py-2 text-sm" type="submit">
                        {invite ? "Continue" : "Sign in"}
                    </button>
                    {message && <p className="text-sm text-red-700" role="alert">{message}</p>}
                </form>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8" data-testid="responder-home">
            <div className="flex items-center justify-between">
                <h1 className="font-heading text-2xl font-bold text-slate-900">Requests from estates</h1>
                <button className="text-sm underline" onClick={() => { sessionStorage.removeItem(KEY); setToken(null); }}>
                    Sign out
                </button>
            </div>
            <p className="text-sm text-slate-600 mt-2">
                You see only what an estate has granted you, for as long as the grant lasts.
            </p>
            {message && <p className="mt-3 text-sm text-slate-700" role="status">{message}</p>}
            {requests.length === 0 && <p className="mt-6 text-sm text-slate-600">Nothing is currently granted to you.</p>}
            {requests.map((r) => {
                const id = `${r.grant_id}/${r.case_id}`;
                const draft = drafts[id] || {};
                return (
                    <div key={id} className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid={`responder-request-${r.case_id}`}>
                        <div className="text-xs uppercase tracking-wide text-slate-500">{r.category || "request"}</div>
                        <p className="mt-1 text-sm text-slate-900">{r.purpose}</p>
                        <p className="mt-1 text-xs text-slate-500">Access ends {new Date(r.expires_at).toLocaleDateString("en-GB")}</p>
                        {draft.sent ? (
                            <p className="mt-3 text-sm text-green-700">Reply sent.</p>
                        ) : (
                            <div className="mt-3 space-y-2">
                                <textarea className="w-full border rounded px-3 py-2 text-sm" rows={3} maxLength={2000} placeholder="Your reply"
                                    value={draft.text || ""} onChange={(e) => setDrafts({ ...drafts, [id]: { ...draft, text: e.target.value } })} />
                                <input className="w-40 border rounded px-3 py-2 text-sm" inputMode="decimal" placeholder="Price £ (optional)"
                                    value={draft.amount || ""} onChange={(e) => setDrafts({ ...drafts, [id]: { ...draft, amount: e.target.value } })} />
                                <button className="block rounded bg-slate-900 text-white px-4 py-2 text-sm" onClick={() => send(r)}>
                                    Send reply
                                </button>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
